// LE PROBLÈME QUALIFIÉ ET SA PRIORITÉ, dans la carte d'un insight ouverte.
//
// Trois choses saisies d'un clic (type, impact, coût), le reste calculé par le
// serveur depuis les preuves : combien de personnes, quels personas, quelle
// confiance. Le score se lit avec son calcul — il doit pouvoir se défendre.
import { useId } from 'react';
import { useObjectives, useUpdateInsight } from '../api/queries';
import type { Insight } from '../api/types';
import { ErrorBanner, Select } from '../components/ui';
import { COSTS, FREQUENCY_LABEL, IMPACTS, PROBLEM_TYPES } from './labels';

const fmt = (n: number) => String(n).replace('.', ',');

function InlineSelect<T extends string>({
  label, value, options, disabled, onChange, emptyLabel = 'à préciser',
}: {
  label: string;
  value: T | null;
  options: { value: T; label: string }[];
  disabled: boolean;
  onChange: (v: T | null) => void;
  /** Libellé de « rien de choisi ». « à préciser » se colore : c'est une valeur estimée. */
  emptyLabel?: string;
}) {
  const id = useId();
  return (
    <div className="problem-field">
      <label className="problem-label" htmlFor={id}>{label}</label>
      <Select
        id={id}
        className={`problem-select${value || emptyLabel !== 'à préciser' ? '' : ' problem-select-empty'}`}
        value={value ?? ''}
        disabled={disabled}
        onChange={(e) => onChange((e.target.value || null) as T | null)}
      >
        <option value="">{emptyLabel}</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </Select>
    </div>
  );
}

export function ProblemPanel({ insight, projectId, canWrite }: { insight: Insight; projectId: string; canWrite: boolean }) {
  const update = useUpdateInsight(projectId);
  const objectives = useObjectives(projectId).data?.objectives ?? [];
  const { problem } = insight;
  const { frequency, confidence, priority } = problem;
  const save = (patch: Record<string, unknown>) => update.mutate({ id: insight.id, patch });

  return (
    <section className="problem" aria-label="Problème et priorité">
      <div className="problem-fields">
        <InlineSelect label="Type" value={insight.problem_type} options={PROBLEM_TYPES} disabled={!canWrite || update.isPending} onChange={(v) => save({ problemType: v })} />
        <InlineSelect label="Impact métier" value={insight.impact} options={IMPACTS} disabled={!canWrite || update.isPending} onChange={(v) => save({ impact: v })} />
        <InlineSelect label="Coût de résolution" value={insight.cost} options={COSTS} disabled={!canWrite || update.isPending} onChange={(v) => save({ cost: v })} />
        {/* L'objectif du projet qu'il sert (migration 049) : il fait monter la priorité. */}
        {objectives.length > 0 ? (
          <InlineSelect
            label="Objectif servi"
            value={insight.objective_id}
            options={objectives.map((o) => ({ value: o.id, label: o.title }))}
            disabled={!canWrite || update.isPending}
            onChange={(v) => save({ objectiveId: v })}
            emptyLabel="aucun"
          />
        ) : null}
      </div>

      <dl className="problem-facts">
        <div>
          <dt>Fréquence</dt>
          <dd>
            {frequency.touched} personne{frequency.touched > 1 ? 's' : ''}
            {frequency.total ? ` sur ${frequency.total} rencontrée${frequency.total > 1 ? 's' : ''}` : ''}
            {' · '}{FREQUENCY_LABEL[frequency.level]}
          </dd>
        </div>
        <div>
          <dt>Qui</dt>
          <dd>{problem.personas.length > 0 ? problem.personas.join(', ') : 'Aucun persona rattaché aux preuves'}</dd>
        </div>
        <div>
          <dt>Confiance</dt>
          <dd>
            {confidence.level ?? 'aucune preuve'}
            {confidence.checks.length > 0 ? (
              <ul className="problem-checks">
                {confidence.checks.map((c) => (
                  <li key={c.code} className={c.ok ? 'is-ok' : 'is-missing'}>
                    <span aria-hidden="true">{c.ok ? '✓' : '·'}</span> {c.text}
                  </li>
                ))}
              </ul>
            ) : null}
          </dd>
        </div>
      </dl>

      {priority ? (
        <p className="problem-score">
          <strong>Score {fmt(priority.score)}</strong>
          <span className="problem-formula">
            gravité {priority.parts.gravity} × fréquence {priority.parts.frequency} × impact {priority.parts.impact}
            {' ÷ '}coût {priority.parts.cost} × confiance {fmt(priority.parts.confidence)}
            {priority.parts.alignment !== 1 ? ` × objectif ${fmt(priority.parts.alignment)}` : ''}
          </span>
          {priority.estimated.length > 0 ? (
            <span className="problem-estimated">
              Estimé au niveau moyen faute de saisie : {priority.estimated.join(', ')}.
            </span>
          ) : null}
        </p>
      ) : (
        <p className="problem-score problem-estimated">Pas de priorité sans preuve : on ne classe pas une opinion.</p>
      )}
      {update.error ? <ErrorBanner>Enregistrement impossible.</ErrorBanner> : null}
    </section>
  );
}
